import { memo } from 'react';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { isExternalUrl, isSafeUrl, resolveDocumentUrl } from '../../markdown/security/sanitizeSchema';
import type { MarkdownLinkProps } from '../../types';

/**
 * Renders a Markdown link with URL policy applied at the component boundary.
 *
 * The sanitizer already strips dangerous protocols; re-checking here means the
 * component stays safe even when used outside the pipeline, and gives us a place
 * to force `rel="noopener noreferrer"` on cross-origin targets.
 */
export const MarkdownLink = memo(function MarkdownLink({
  href,
  title,
  id,
  children,
  allowExternal = true,
  baseUrl,
  footnoteRef,
  footnoteBackref,
}: MarkdownLinkProps) {
  const safe = isSafeUrl(href, baseUrl);
  const external = safe && isExternalUrl(href, baseUrl);

  if (!safe) {
    return (
      <Tooltip title="This link was blocked because it uses an unsupported URL scheme.">
        <Box
          component="span"
          className="blocked-link"
          data-testid="blocked-link"
          sx={{
            textDecoration: 'line-through',
            cursor: 'not-allowed',
            color: 'text.disabled',
          }}
        >
          {children}
        </Box>
      </Tooltip>
    );
  }

  if (external && !allowExternal) {
    return (
      <Tooltip title="External links are disabled for this document.">
        <Box component="span" sx={{ color: 'text.disabled', cursor: 'not-allowed' }}>
          {children}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Link
      href={href ? resolveDocumentUrl(href, baseUrl) : undefined}
      title={title}
      id={id}
      underline="none"
      {...(footnoteRef ? { 'data-footnote-ref': '' } : {})}
      {...(footnoteBackref ? { 'data-footnote-backref': '', className: 'data-footnote-backref' } : {})}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer nofollow ugc' } : {})}
      sx={{ scrollMarginTop: '84px' }}
    >
      {children}
      {external && (
        <>
          <OpenInNewRoundedIcon
            aria-hidden
            sx={{ fontSize: '0.75em', ml: '0.22em', verticalAlign: 'baseline', opacity: 0.65 }}
          />
          <Box component="span" className="sr-only">
            {' (opens in a new tab)'}
          </Box>
        </>
      )}
    </Link>
  );
});

export default MarkdownLink;
